import {
  AdminInput,
  AdminSelect,
  AdminSpinner,
  ANNOUNCEMENT_APPS,
  ANNOUNCEMENT_PLATFORMS,
  AnnouncementApp,
  AnnouncementPlatform,
  Div,
  H5,
  ListSpinner,
  TableHeader,
  UserRole,
  renderRole,
  useDebouncedValue,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { NoDataComponent } from '../../../adminComponents/NoDataComponent/NoDataComponent';
import { platformLabel } from '../helpers/announcementViewers';
import {
  NO_VIEWER_FILTERS,
  ViewerFilters,
  useAnnouncementViewers,
} from '../hooks/useAnnouncementViewers';
import { AnnouncementViewerRow, VIEWER_COLUMNS } from './AnnouncementViewerRow';

type Props = {
  id: number;
  seenCount: number;
};

/**
 * Everyone who saw it, with search and the app / platform / role filters the
 * endpoint takes. The header carries the all-time count; a filtered list has
 * no total of its own, so it does not pretend to one.
 */
export const AnnouncementViewers = ({ id, seenCount }: Props) => {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 400);
  const [filters, setFilters] =
    useState<Omit<ViewerFilters, 'query'>>(NO_VIEWER_FILTERS);
  const list = useAnnouncementViewers(id, { ...filters, query });
  const filtered = !!(query || filters.app || filters.platform || filters.role);

  const [sentinelRef, sentinelInView] = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (sentinelInView && list.hasNextPage && !list.isFetchingNextPage) {
      list.fetchNextPage();
    }
  }, [sentinelInView, list]);

  const setFilter = <Key extends keyof typeof filters>(
    key: Key,
    value: (typeof filters)[Key]
  ) => setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    // The page stacks its cards 8px apart on a phone, which is right between
    // cards but leaves this bare section title glued to the one above.
    <Div $mSM="16px 0 0">
      <H5 $m="0 0 16px">Viewers ({seenCount})</H5>

      <Div
        $grid
        $gridTemplateColumns="2fr 1fr 1fr 1fr"
        $gridTemplateColumnsSM="minmax(0, 1fr)"
        $gap="16px"
        $gapSM="8px"
        $mb="20"
      >
        <AdminInput
          value={search}
          placeholder="Search name or email"
          search
          shadowDesign
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
        />
        <AdminSelect
          value={filters.app}
          onChange={(e) =>
            setFilter('app', e.target.value as AnnouncementApp | '')
          }
          options={
            <>
              <option value="">All apps</option>
              {ANNOUNCEMENT_APPS.map((app) => (
                <option key={app} value={app}>
                  {app}
                </option>
              ))}
            </>
          }
          $shadowDesign
        />
        <AdminSelect
          value={filters.platform}
          onChange={(e) =>
            setFilter('platform', e.target.value as AnnouncementPlatform | '')
          }
          options={
            <>
              <option value="">All platforms</option>
              {ANNOUNCEMENT_PLATFORMS.map((platform) => (
                <option key={platform} value={platform}>
                  {platformLabel(platform)}
                </option>
              ))}
            </>
          }
          $shadowDesign
        />
        <AdminSelect
          value={filters.role}
          onChange={(e) => setFilter('role', e.target.value as UserRole | '')}
          options={
            <>
              <option value="">All roles</option>
              {Object.values(UserRole).map((role) => (
                <option key={role} value={role}>
                  {renderRole(role)}
                </option>
              ))}
            </>
          }
          $shadowDesign
        />
      </Div>

      {list.isLoading ? (
        <AdminSpinner />
      ) : (
        <Div $relative>
          {list.isFetching && !list.isFetchingNextPage && (
            <AdminSpinner small absolute creme />
          )}
          <TableHeader columns={VIEWER_COLUMNS} />
          {list.viewers.map((viewer) => (
            <AnnouncementViewerRow key={viewer.userId} viewer={viewer} />
          ))}
          {!list.viewers.length && (
            <NoDataComponent
              height="25vh"
              heightSM="25vh"
              title={filtered ? 'No matches' : 'No views yet'}
              description={
                filtered
                  ? 'Nobody who saw it fits these filters.'
                  : 'Nobody has seen this announcement yet.'
              }
            />
          )}
        </Div>
      )}

      <ListSpinner
        loading={list.isFetchingNextPage}
        observerRef={sentinelRef}
      />
    </Div>
  );
};
